import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { Avatar, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import CustomButton from '@src/components/CustomButton/CustomButton';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import PageHeader from '@src/components/PageHeader/PageHeader';
import StyledCustomAvatar from '@src/components/Avatar/StyledAvatar';
import {
	deepEqual,
	generateInitialsHelper,
	validateEmail,
	validateFileSize,
} from '@src/helpers/Helpers';
import { CustomAlertType } from '@src/interfaces/type';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { setCurrentUser } from '@src/store/reducers/currentUserReducer';
import { addPlus } from '@src/helpers/PhoneNumberHelper';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	overflow: scroll;
	margin: 30px;
	gap: 24px;
	@media screen and (max-width: 600px) {
		margin: 16px;
	}
`;

const Profile = styled.div`
	display: flex;
	align-items: center;
	gap: 15px;
	p {
		font-size: 20px;
		font-weight: 600;
		line-height: 27px;
	}
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 25px;
	width: 100%;
`;

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 0px;
`;

interface User {
	name: string;
	profile?: string;
	email: string;
}
interface Props {
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
}
const GeneralSettings: React.FC<Props> = ({ setAlert, displayCustomError }) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const dispatch = useAppDispatch();
	const currentUser: any = useAppSelector((state: any) => state?.currentUser?.value);
	const [loading, setLoading] = useState(false);
	const [user, setUser] = useState<User>({
		name: '',
		profile: '',
		email: '',
	});
	const [initialFieldsData, setInitialFieldsData] = useState({
		name: '',
		email: '',
	});
	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});
	const isMobileWidth = IsMobileWidth();

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const handleFileChange = (files: FileList | null) => {
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
			} else {
				displayCustomError('File Size Exceeds');
			}
		}
	};

	const checkChanges = () => {
		const updatedDataCopy = {
			name: user?.name,
			email: user?.email,
		};

		return deepEqual(initialFieldsData, updatedDataCopy);
	};

	const updateProfilePhoto = async (files: FileList | null) => {
		try {
			if (files) {
				// Check file size
				if (files?.[0]?.size > 1 * 1024 * 1024) {
					displayCustomError('File size exceeds 1MB limit');
					return;
				}

				const allowedFormats = ['jpg', 'jpeg', 'png'];
				const fileExtension: string | undefined = files?.[0]?.name.split('.').pop()?.toLowerCase();

				if (fileExtension && !allowedFormats.includes(fileExtension)) {
					displayCustomError('Invalid file format. Allowed formats: JPG, JPEG, PNG');
					return;
				}

				const formData = new FormData();
				formData.append('file', files?.[0]);

				setLoading(true);
				apiService.updateProfilePhotoCallV2(
					formData,
					(response: AxiosResponse) => {
						setUser({ ...user, profile: response?.data?.data });
						dispatch(setCurrentUser({ ...currentUser, profile: response?.data?.data }));
						setAlert({
							message: 'Profile Picture Updated Successfully',
							severity: 'success',
						});
						setLoading(false);
					},
					(error: AxiosResponse) => {
						displayCustomError('Something went wrong!');
						setLoading(false);
					}
				);
			}
		} catch (error) {
			// Handle exceptions here
		} finally {
			setLoading(false);
		}
	};

	const updateUserProfile = () => {
		setLoading(true);
		const data = {
			email: user?.email?.trim(),
			name: user?.name?.trim(),
		};
		const isValidEmail = validateEmail(data?.email);

		if (isValidEmail) {
			apiService?.updateProfile(
				data,
				(response: AxiosResponse) => {
					setAlert({
						message: 'Profile Updated Successfully',
						severity: 'success',
					});
					setLoading(false);
					dispatch(setCurrentUser({ ...currentUser, firstName: data?.name, email: data?.email }));
					setInitialFieldsData({
						...initialFieldsData,
						name: data?.name,
						email: data?.email,
					});
				},
				(error: AxiosError) => {
					setLoading(false);
					displayCustomError('Something went wrong!');
				}
			);
		} else {
			displayCustomError('The email provided is invalid. Please enter a valid email address.');
			setLoading(false);
		}
	};

	useEffect(() => {
		setUser({
			...user,
			name: currentUser?.firstName,
			profile: currentUser?.profile,
			email: currentUser?.email,
		});
		setInitialFieldsData({
			...initialFieldsData,
			name: currentUser?.firstName,
			email: currentUser?.email,
		});
	}, []);

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	if (isMobileWidth) {
		return (
			<div
				style={{
					overflowY: 'auto',
					overflowX: 'hidden',
					flexGrow: 1,
					height: 'calc(100vh - 180px)',
				}}
			>
				<StyledContainer>
					<Profile>
						<StyledCustomAvatar
							image={currentUser?.profile}
							submitLogo={updateProfilePhoto}
							style={{ width: '94px', height: '94px' }}
							generateBgColorBy={currentUser?.firstName}
						>
							<h2>
								{!currentUser?.profile ? generateInitialsHelper(currentUser?.firstName) : null}
							</h2>
						</StyledCustomAvatar>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<p>{currentUser?.firstName}</p>
							<span
								style={{
									fontSize: '12px',
									color: 'rgba(0, 0, 45, 0.45)',
								}}
							>
								{addPlus(currentUser?.phone)}
							</span>
						</div>
					</Profile>
					<StyledForm>
						<LabeledTextField
							label="Name"
							value={user?.name}
							styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setUser({ ...user, name: e?.target?.value as string })
							}
							placeholder="Name"
						/>
						<LabeledTextField
							label="Email"
							value={user?.email}
							styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
								setUser({ ...user, email: e?.target?.value as string })
							}
							placeholder="Email"
						/>
						<LabeledTextField
							label="Mobile No."
							value={currentUser?.phone}
							styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
							disabled
						/>
						<LabeledTextField
							label="Account Name"
							value={currentUser?.organization?.name}
							styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
							disabled
						/>
						<LabeledTextField
							label="Account Type"
							value={currentUser?.organization?.type}
							styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
							disabled
						/>
					</StyledForm>
					{/* filler View */}
					<div style={{ height: '40px' }}></div>
				</StyledContainer>
				<StyledBottomBtnContainer>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
						}}
					>
						<CustomButton
							title={
								loading ? (
									<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
								) : (
									'Save'
								)
							}
							color="primary"
							styles={{ height: '40px', width: '328px' }}
							handleClick={updateUserProfile}
							disabled={loading || checkChanges()}
						/>
					</div>
				</StyledBottomBtnContainer>
			</div>
		);
	}

	return (
		<StyledContainer>
			<Profile>
				<StyledCustomAvatar
					image={currentUser?.profile}
					submitLogo={updateProfilePhoto}
					style={{ width: '94px', height: '94px' }}
					generateBgColorBy={currentUser?.firstName}
				>
					<h2>{!currentUser?.profile ? generateInitialsHelper(currentUser?.firstName) : null}</h2>
				</StyledCustomAvatar>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<p>{currentUser?.firstName}</p>
					<span
						style={{
							fontSize: '12px',
							color: 'rgba(0, 0, 45, 0.45)',
						}}
					>
						{addPlus(currentUser?.phone)}
					</span>
				</div>
			</Profile>
			<StyledForm>
				<LabeledTextField
					label="Name"
					value={user?.name}
					styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						setUser({ ...user, name: e?.target?.value as string })
					}
					placeholder="Name"
				/>
				<LabeledTextField
					label="Email"
					value={user?.email}
					styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						setUser({ ...user, email: e?.target?.value as string })
					}
					placeholder="Email"
				/>
				<LabeledTextField
					label="Mobile No."
					value={currentUser?.phone}
					styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
					disabled
				/>
				<LabeledTextField
					label="Account Name"
					value={currentUser?.organization?.name}
					styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
					disabled
				/>
				<LabeledTextField
					label="Account Type"
					value={
						currentUser?.organization?.type === 'RESTAURANT'
							? 'DEALER'
							: currentUser?.organization?.type
					}
					styles={{ width: windowDimensions?.width > 800 ? '729px' : '90vw', height: '36px' }}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
					disabled
				/>
				<CustomButton
					title={
						loading ? (
							<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
						) : (
							'Save'
						)
					}
					color="primary"
					styles={{ height: '40px', width: '328px' }}
					handleClick={updateUserProfile}
					disabled={loading || checkChanges()}
				/>
			</StyledForm>
		</StyledContainer>
	);
};

export default GeneralSettings;
